import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { CachePersistentService } from './cache-persistent.service';

type CacheStrategy = 'persistent' | 'memory' | 'network-only';

interface StrategyConfig {
  strategy: CacheStrategy;
  ttlMinutes?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SmartCacheService {
  private cachePersistent = inject(CachePersistentService);
  private memoryCache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly MEMORY_TTL = 15 * 60 * 1000; // 15 minutos

  /**
   * Mapea endpoints a su estrategia de caché
   */
  private strategies: Record<string, StrategyConfig> = {
    // Métodos sin parámetros: caché persistente de 15 minutos
    obtenerConfidencialidad: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerTipoBusqueda: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerModalidad: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerJornada: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerMotivos: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerMinutosColacion: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerTiposProceso: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerCentroCosto: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerPaises: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerOficina: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerZona: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerEjecutivo: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerArea: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerGerencia: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerHabilidades: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerTiposRol: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerUrgencias: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerTiposActividad: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerMotivosDescarte: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerEjecutivos: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerBonos: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerTiposContrato: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerComunas: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerRegiones: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerNacionalidades: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerGeneros: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerBancos: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerBancoTiposCuenta: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerEstadosCiviles: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerParentezcos: { strategy: 'persistent', ttlMinutes: 15 },
    obtenerPerfiles: { strategy: 'persistent', ttlMinutes: 15 },

    // Métodos con parámetros dinámicos: caché en memoria de 15 minutos
    obtenerEmpresas: { strategy: 'memory', ttlMinutes: 15 },
    obtenerEjecutivosRut: { strategy: 'memory', ttlMinutes: 15 },
    obtenerEstadosSolicitud: { strategy: 'memory', ttlMinutes: 15 },
    obtenerParamsInfProv: { strategy: 'memory', ttlMinutes: 15 },

    // S3 URLs: nunca cachear
    getSignedUrl: { strategy: 'network-only' },
  };

  /**
   * Obtiene o genera una clave de caché basada en el endpoint y parámetros
   */
  private getCacheKey(endpoint: string, params?: HttpParams): string {
    if (!params) {
      return endpoint;
    }

    // Serializar parámetros para incluir en la clave
    const paramStr = params
      .keys()
      .sort()
      .map((key) => `${key}=${params.get(key)}`)
      .join('&');
    return `${endpoint}?${paramStr}`;
  }

  /**
   * Obtiene la estrategia de caché para un endpoint
   */
  private getStrategy(endpoint: string): StrategyConfig {
    // Buscar coincidencia exacta o por prefijo
    for (const [key, strategy] of Object.entries(this.strategies)) {
      if (endpoint.includes(key)) {
        return strategy;
      }
    }
    // Por defecto: caché en memoria de 15 minutos
    return { strategy: 'memory', ttlMinutes: 15 };
  }

  /**
   * Obtiene datos del caché según la estrategia configurada
   */
  async get<T>(endpoint: string, params?: HttpParams): Promise<T | null> {
    const strategy = this.getStrategy(endpoint);
    const cacheKey = this.getCacheKey(endpoint, params);

    if (strategy.strategy === 'network-only') {
      return null;
    }

    if (strategy.strategy === 'memory') {
      return this.getFromMemoryCache<T>(cacheKey);
    }

    // persistent
    return this.cachePersistent.get<T>(cacheKey);
  }

  /**
   * Almacena datos en el caché según la estrategia configurada
   */
  async set<T>(endpoint: string, data: T, params?: HttpParams): Promise<void> {
    const strategy = this.getStrategy(endpoint);
    const cacheKey = this.getCacheKey(endpoint, params);

    if (strategy.strategy === 'network-only') {
      return; // No cachear URLs de S3
    }

    if (strategy.strategy === 'memory') {
      this.setMemoryCache<T>(cacheKey, data);
      return;
    }

    // persistent
    const ttl = strategy.ttlMinutes || 1440;
    await this.cachePersistent.set<T>(cacheKey, data, ttl);
  }

  /**
   * Obtiene del caché en memoria
   */
  private getFromMemoryCache<T>(key: string): T | null {
    const entry = this.memoryCache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.MEMORY_TTL) {
      this.memoryCache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Almacena en caché en memoria
   */
  private setMemoryCache<T>(key: string, data: T): void {
    this.memoryCache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Limpia todo el caché (memoria + persistente)
   */
  async clear(): Promise<void> {
    this.memoryCache.clear();
    await this.cachePersistent.clear();
    console.log('Caché completamente limpiado');
  }

  /**
   * Limpia solo el caché de memoria
   */
  clearMemory(): void {
    this.memoryCache.clear();
    console.log('Caché en memoria limpiado');
  }

  /**
   * Limpia solo el caché persistente
   */
  async clearPersistent(): Promise<void> {
    await this.cachePersistent.clear();
    console.log('Caché persistente limpiado');
  }

  /**
   * Obtiene estadísticas del caché
   */
  async getStats(): Promise<{
    memoryEntries: number;
    persistentSize: number;
  }> {
    return {
      memoryEntries: this.memoryCache.size,
      persistentSize: await this.cachePersistent.getSize(),
    };
  }

  /**
   * Limpia cachés expirados
   */
  async cleanExpired(): Promise<void> {
    await this.cachePersistent.cleanExpired();
    console.log('Cachés expirados limpiados');
  }
}
